import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Loader2, Compass, Heart, Eye, Users, Sprout, Zap, ArrowRight, ArrowLeft,
  RotateCcw, Lock, ChevronDown, Sparkles, Calendar, MapPin, LineChart,
  Brain, Leaf, Utensils, Footprints, HeartHandshake, FlaskConical,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import BookingModal from "@/components/BookingModal";
import { toast } from "sonner";
import { fmtDate, money } from "@/lib/format";
import GaugeDeeper from "@/components/gauge/GaugeDeeper";

import { toPlainText } from "@/components/RichContent";
const SAVE_KEY = "eyw-gauge-progress";

const ORIENTATION = {
  gratitude: { icon: Heart, color: "#D98E73", label: "Gratitude" },
  awareness: { icon: Eye, color: "#144F75", label: "Awareness" },
  unity: { icon: Users, color: "#8FA89B", label: "Unity" },
  growth: { icon: Sprout, color: "#5B8A72", label: "Growth" },
  energy: { icon: Zap, color: "#B5794E", label: "Energy" },
};
const ENERGY_COPY = { limited: "may be limited", abundant: "feels supported", excessive: "may be over-extended" };
const DIM_META = {
  mind: { icon: Brain, color: "#144F75" },
  body: { icon: Zap, color: "#B5794E" },
  emotional: { icon: Heart, color: "#D98E73" },
};
const CAT_ICON = {
  "Daily habits": Sparkles, "Meditation": Compass, "Foods": Utensils,
  "Herbs": Leaf, "Nature walks": Footprints, "Relationship stewardship": HeartHandshake,
};

export default function GaugeApp() {
  const { user } = useAuth();
  const [stage, setStage] = useState("intro"); // intro | questions | loading | result
  const [selectedPath, setSelectedPath] = useState(null);
  const [responses, setResponses] = useState({});
  const [history, setHistory] = useState([]); // asked questions, in order
  const [current, setCurrent] = useState(null);
  const [transition, setTransition] = useState("");
  const [progressInfo, setProgressInfo] = useState({ number: 1, total: 9 });
  const [scale, setScale] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);
  const [bookingService, setBookingService] = useState(null);
  const [openPractice, setOpenPractice] = useState(null);

  const paths = useQuery({ queryKey: ["gauge-paths"], queryFn: () => api.get("/gauge/paths").then((r) => r.data) });

  // Keep in-progress answers in this browser tab only (private; cleared when the tab closes or on finish).
  useEffect(() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(SAVE_KEY) || "null");
      if (s?.selectedPath && ["questions", "review"].includes(s.stage)) {
        setSelectedPath(s.selectedPath); setResponses(s.responses || {}); setHistory(s.history || []);
        setCurrent(s.current || null); setScale(s.scale || []); setProgressInfo(s.progressInfo || { number: 1, total: 9 }); setStage(s.stage);
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (["questions", "review"].includes(stage)) sessionStorage.setItem(SAVE_KEY, JSON.stringify({ stage, selectedPath, responses, history, current, scale, progressInfo }));
    else if (stage === "result" || stage === "intro") sessionStorage.removeItem(SAVE_KEY);
  }, [stage, selectedPath, responses, history, current, scale, progressInfo]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage, current?.id]);

  const progress = progressInfo.total ? Math.round((progressInfo.number / progressInfo.total) * 100) : 0;

  const fetchNext = async (path, resps, asked) => {
    setFetching(true);
    try {
      const { data } = await api.post("/gauge/exploration/next", {
        path_slug: path.slug, responses: resps, asked: asked.map((q) => String(q.id)),
      });
      setScale(data.response_scale || []);
      if (data.done || !data.question) return { done: true };
      setCurrent(data.question);
      setTransition(data.transition || "");
      setProgressInfo(data.progress || { number: asked.length + 1, total: 9 });
      return { done: false };
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      return { error: true };
    } finally { setFetching(false); }
  };

  const startPath = async (p) => {
    setSelectedPath(p); setResponses({}); setHistory([]); setCurrent(null); setTransition("");
    setStage("questions");
    await fetchNext(p, {}, []);
  };
  const setAnswer = (qid, val) => setResponses((r) => ({ ...r, [qid]: val }));

  const restart = () => {
    setStage("intro"); setSelectedPath(null); setResponses({}); setHistory([]); setCurrent(null);
    setTransition(""); setResult(null); setOpenPractice(null);
  };

  const submit = async (resps) => {
    setStage("loading");
    try {
      const { data } = await api.post("/gauge/explore", { path_slug: selectedPath.slug, responses: resps });
      setResult(data);
      setStage("result");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setStage("review");
    }
  };

  const next = async (resps = responses) => {
    if (!current) return;
    const newHistory = [...history, current];
    setHistory(newHistory);
    const r = await fetchNext(selectedPath, resps, newHistory);
    if (r.done) { setCurrent(null); setStage("review"); }
    else if (r.error) setHistory(history);
  };
  const skip = () => { const r = { ...responses }; delete r[current.id]; setResponses(r); next(r); };
  const notSure = () => { const r = { ...responses, [current.id]: "not_sure" }; setResponses(r); next(r); };

  const back = () => {
    if (history.length === 0) { restart(); return; }
    const prev = history[history.length - 1];
    setResponses((r) => { const c = { ...r }; delete c[current?.id]; return c; });
    setHistory(history.slice(0, -1));
    setCurrent(prev);
    setTransition("");
    setProgressInfo((p) => ({ ...p, number: Math.max(1, p.number - 1) }));
  };

  // ---------------------------------------------------------------- INTRO
  if (stage === "intro") {
    return (
      <div data-testid="gauge-exploration" className="min-h-[70vh]">
        <section className="relative py-20 bg-[#144F75] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <Compass className="mx-auto text-[#D98E73] mb-5" size={40} />
            <p className="eyw-label !text-[#D98E73] mb-3">GAUGE Your Life · Exploration</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Where would you like to begin?</h1>
            <p className="text-white/80 text-lg mt-5 max-w-xl mx-auto">
              A gentle, private self-exploration. Choose what feels most alive right now — there are no wrong answers,
              and nothing is diagnosed or scored publicly.
            </p>
            <p className="text-white/60 text-sm mt-4 flex items-center justify-center gap-2">
              <Lock size={14} /> Private by default{user ? " · saved to your account" : " · sign in to save your reflection"}
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
          {paths.isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gauge-path-grid">
              {(paths.data || []).map((p) => (
                <button
                  key={p.slug}
                  onClick={() => startPath(p)}
                  data-testid={`gauge-path-${p.slug}`}
                  className="eyw-card p-6 text-left group transition-transform hover:-translate-y-1 hover:shadow-lg"
                >
                  <h3 className="font-serif text-xl text-[#1A242B] group-hover:text-[#144F75] transition-colors">{p.title}</h3>
                  <p className="text-[#4A5D6B] text-sm mt-2 leading-relaxed">{p.public_label}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#D98E73] text-sm font-medium">
                    Begin <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  // ---------------------------------------------------------------- REVIEW
  if (stage === "review") {
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-10 pb-24" data-testid="gauge-review">
        <p className="eyw-label">{selectedPath?.title}</p>
        <h1 className="font-serif text-3xl text-[#1A242B] mt-1">Review your answers</h1>
        <p className="text-[#4A5D6B] text-sm mt-2">Change anything that doesn't feel right. Skipped questions are fine to leave blank.</p>
        <div className="space-y-4 mt-6">
          {history.map((q) => (
            <div key={q.id} className="eyw-card p-5" data-testid={`gauge-review-${q.id}`}>
              <p className="font-medium text-[#1A242B] mb-3">{q.question_text}</p>
              {q.question_type === "textarea" ? (
                <textarea rows={2} value={responses[q.id] === "not_sure" ? "" : responses[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)}
                  aria-label={q.question_text} data-testid={`gauge-review-text-${q.id}`} className="eyw-input w-full resize-none" placeholder="Skipped" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(q.question_type === "frequency" ? scale : q.options || []).map((opt) => {
                    const multi = q.question_type === "multi_select";
                    const arr = Array.isArray(responses[q.id]) ? responses[q.id] : [];
                    const on = multi ? arr.includes(opt.key) : responses[q.id] === opt.key;
                    return <button key={opt.key} type="button" aria-pressed={on} data-testid={`gauge-review-opt-${q.id}-${opt.key}`}
                      onClick={() => setAnswer(q.id, multi ? (on ? arr.filter((k) => k !== opt.key) : [...arr, opt.key]) : (on ? "" : opt.key))}
                      className={`px-3 py-1.5 rounded-full border text-sm ${on ? "border-[#144F75] bg-[#144F75]/[0.08] text-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{opt.label}</button>;
                  })}
                </div>
              )}
              {responses[q.id] === "not_sure" && <p className="text-xs text-[#8FA89B] mt-2">You chose "I'm not sure"</p>}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-between gap-3 mt-8">
          <button onClick={restart} data-testid="gauge-review-restart" className="eyw-btn-outline"><RotateCcw size={16} /> Start over</button>
          <button onClick={() => submit(responses)} disabled={!Object.values(responses).some((v) => (Array.isArray(v) ? v.length : v))} data-testid="gauge-review-submit" className="eyw-btn disabled:opacity-60">See my reflection <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- LOADING
  if (stage === "loading") {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="gauge-loading">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#144F75] mx-auto mb-4" size={36} />
          <p className="font-serif text-2xl text-[#1A242B]">Reflecting on what you shared…</p>
          <p className="text-[#4A5D6B] mt-2">Finding a supportive next step.</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- RESULT
  if (stage === "result" && result) {
    const o = ORIENTATION[result.primary_orientation] || ORIENTATION.awareness;
    const OIcon = o.icon;
    const recs = result.recommendations || {};
    return (
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12" data-testid="gauge-result">
        <div className="eyw-card overflow-hidden">
          <div className="p-8 lg:p-10 text-center" style={{ background: `linear-gradient(160deg, ${o.color}14, transparent)` }}>
            <span className="grid place-items-center h-20 w-20 rounded-2xl mx-auto text-white font-serif text-4xl" style={{ background: o.color }}>{result.letter}</span>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium" style={{ color: o.color }}>
              <OIcon size={18} /> {o.label} is asking for attention
            </span>
            <h1 className="font-serif text-3xl lg:text-4xl text-[#1A242B] mt-3">{result.result.title}</h1>
            <p className="text-[#4A5D6B] mt-4 leading-relaxed max-w-2xl mx-auto">{result.result.summary}</p>
          </div>

          <div className="px-8 lg:px-10 pb-8">
            <div className="rounded-xl border-l-4 border-[#D98E73] bg-[#D98E73]/[0.07] px-5 py-4 mt-2">
              <p className="eyw-label !text-[#D98E73] mb-1">A gentle next step</p>
              <p className="text-[#144F75] leading-relaxed">{result.result.guidance}</p>
            </div>

            {result.top_focus_areas?.length > 0 && (
              <div className="mt-6">
                <p className="eyw-label mb-2">What stood out</p>
                <div className="flex flex-wrap gap-2">
                  {result.top_focus_areas.map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-[#144F75]/[0.06] text-[#144F75] text-sm">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {result.energy_relationships && Object.keys(result.energy_relationships).length > 0 && (
              <div className="mt-5">
                <p className="eyw-label mb-2">Your energy right now</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.energy_relationships).map(([domain, rel]) => (
                    <span key={domain} className="px-3 py-1 rounded-full bg-[#8FA89B]/15 text-[#4A5D6B] text-sm capitalize">
                      {domain.replace(/_/g, " ")} — {ENERGY_COPY[rel] || rel}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <GaugeDeeper result={result} path={selectedPath} responses={responses} saved={!!result.saved} />

        {/* Recommendations */}
        <h2 className="font-serif text-2xl text-[#1A242B] mt-10 mb-1">{result.result.next_step_heading}</h2>
        <p className="text-[#4A5D6B] text-sm mb-5">Real options from across the ecosystem that connect with what you shared. Free practices are just as valid a place to start as anything you book.</p>
        <MatchContext ctx={recs.match_context} />
        {!recs.services?.length && !recs.events?.length && !recs.community?.length && !recs.courses?.length && (
          <p className="text-sm text-[#4A5D6B] eyw-card p-4 mb-5" data-testid="gauge-no-match">We don't have an offering that closely matches this right now. The free practices below, and our community, are open to you anytime.</p>
        )}

        {(recs.courses || []).length > 0 && (
          <div className="mb-5" data-testid="gauge-recommended-courses">
            <p className="text-sm font-semibold text-[#1A242B] mb-2">Courses matched to your GAUGE results</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {recs.courses.map((c) => (
                <Link key={c.id} to={`/courses/${c.slug || c.id}`} data-testid={`gauge-course-${c.id}`}
                  className="eyw-card p-4 flex gap-3 hover:border-[#144F75]/40 transition-colors">
                  {c.thumbnail && <img src={c.thumbnail} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-serif text-base text-[#1A242B] leading-snug truncate">{c.title}</p>
                    <p className="text-xs text-[#4A5D6B] truncate">{c.enhancer_name}{c.access === "free" ? " · Free" : c.price ? ` · ${money(Number(c.price))}` : ""}</p>
                    <p className="text-[11px] text-[#8FA89B] capitalize mt-0.5">Supports: {(c.matched_tags || []).join(", ")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {recs.matched_enhancer && (
          <div className="eyw-card p-6 mb-5 border-l-4 border-[#D98E73] bg-gradient-to-r from-[#D98E73]/[0.06] to-transparent" data-testid="gauge-matched-enhancer">
            <div className="flex items-center justify-between mb-3">
              <p className="eyw-label !text-[#D98E73] flex items-center gap-1.5"><Sparkles size={12} /> Your matched Enhancer</p>
              <span className="text-xs font-semibold text-[#5C8A72] bg-[#5C8A72]/10 px-2.5 py-1 rounded-full">{recs.matched_enhancer.match_score}% fit</span>
            </div>
            <div className="flex items-start gap-4">
              {recs.matched_enhancer.avatar ? (
                <img src={recs.matched_enhancer.avatar} alt={recs.matched_enhancer.name} className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <span className="grid place-items-center h-16 w-16 rounded-full bg-[#144F75]/10 text-[#144F75]"><Users size={26} /></span>
              )}
              <div className="flex-1">
                <h3 className="font-serif text-xl text-[#1A242B]">{recs.matched_enhancer.name}</h3>
                <p className="text-[#4A5D6B] text-sm">{recs.matched_enhancer.headline || recs.matched_enhancer.location}</p>
                <p className="text-[#144F75] text-sm mt-2 italic">{recs.matched_enhancer.reason}</p>
                {recs.matched_enhancer.specialties?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {recs.matched_enhancer.specialties.map((s) => (
                      <span key={s} className="text-xs bg-[#144F75]/[0.06] text-[#144F75] px-2 py-0.5 rounded-full capitalize">{s}</span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  {recs.matched_enhancer.intro_service && (
                    <button onClick={() => setBookingService(recs.matched_enhancer.intro_service)} data-testid="gauge-matched-book" className="eyw-btn !py-2 text-sm">
                      <Calendar size={15} /> Book intro session{recs.matched_enhancer.intro_service.price > 0 ? ` · ${money(recs.matched_enhancer.intro_service.price)}` : ""}
                    </button>
                  )}
                  <Link to={`/enhancers/${recs.matched_enhancer.id}`} data-testid="gauge-matched-connect" className="eyw-btn-outline !py-2 text-sm"><Users size={15} /> View {recs.matched_enhancer.name.split(" ")[0]}'s profile</Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {recs.services?.length > 0 && (
          <div className="space-y-3" data-testid="gauge-rec-services">
            {recs.services.map((s) => (
              <div key={s.id} className="eyw-card p-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="eyw-label mb-0.5">Wellness Service</p>
                  <h3 className="font-serif text-lg text-[#1A242B]">{s.title}</h3>
                  <p className="text-[#4A5D6B] text-sm mt-0.5">{toPlainText(s.short_description || s.description)}</p>
                  <WhyChips why={s.why} testid={`gauge-why-${s.id}`} />
                </div>
                <button onClick={() => setBookingService(s)} data-testid={`gauge-book-${s.id}`} className="eyw-btn whitespace-nowrap">
                  <Calendar size={16} /> {s.price > 0 ? `Book · ${money(s.price)}` : "Book"}
                </button>
              </div>
            ))}
          </div>
        )}

        {recs.enhancers?.filter((e) => e.id !== recs.matched_enhancer?.id).length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="gauge-rec-enhancers">
            {recs.enhancers.filter((e) => e.id !== recs.matched_enhancer?.id).map((e) => (
              <Link key={e.id} to={`/enhancers/${e.id}`} className="eyw-card p-5 flex items-center gap-4 group">
                {e.avatar ? (
                  <img src={e.avatar} alt={e.name} className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75]/10 text-[#144F75]"><Users size={20} /></span>
                )}
                <div>
                  <p className="eyw-label mb-0.5">Enhancer</p>
                  <h3 className="font-serif text-lg text-[#1A242B] group-hover:text-[#144F75]">{e.name}</h3>
                  <p className="text-[#4A5D6B] text-xs">{e.headline || e.location}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {recs.packages?.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="gauge-rec-packages">
            {recs.packages.map((p) => (
              <Link key={p.id} to="/packages" className="eyw-card p-5 group" data-testid={`gauge-package-${p.id}`}>
                <p className="eyw-label mb-0.5">Wellness Package</p>
                <h3 className="font-serif text-lg text-[#1A242B] group-hover:text-[#144F75]">{p.name}</h3>
                <p className="text-[#4A5D6B] text-xs mt-0.5">{p.sessions} sessions{p.price > 0 ? ` · ${money(Number(p.price))}` : ""}{p.owner_name ? ` · ${p.owner_name}` : ""}</p>
              </Link>
            ))}
          </div>
        )}

        {recs.practices?.length > 0 && (
          <div className="space-y-3 mt-3" data-testid="gauge-rec-practices">
            {recs.practices.map((p, i) => (
              <div key={i} className="eyw-card p-5">
                <button onClick={() => setOpenPractice(openPractice === i ? null : i)} className="w-full flex items-center justify-between text-left">
                  <div>
                    <p className="eyw-label mb-0.5 flex items-center gap-1.5"><Sparkles size={12} /> Self-guided practice · {p.estimated_time || "a few minutes"}</p>
                    <h3 className="font-serif text-lg text-[#1A242B]">{p.title}</h3>
                    <p className="text-[#4A5D6B] text-sm mt-0.5">{p.short_description}</p>
                  </div>
                  <ChevronDown size={20} className={`text-[#8FA89B] transition-transform ${openPractice === i ? "rotate-180" : ""}`} />
                </button>
                {openPractice === i && <p className="text-[#4A5D6B] text-sm mt-3 pt-3 border-t border-[#E5E0D8] leading-relaxed">{p.full_instructions}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Connected pathways — Events, Locations, Community */}
        {(recs.events?.length > 0 || recs.locations?.length > 0 || recs.community?.length > 0) && (
          <div className="mt-8" data-testid="gauge-pathways">
            <p className="eyw-label !text-[#D98E73] mb-1">Ways to keep going, together</p>
            <p className="text-[#4A5D6B] text-sm mb-4">These parts of the ecosystem connect naturally with what you shared — no pressure, just open doors.</p>
            <div className="grid md:grid-cols-3 gap-3">
              {recs.events?.length > 0 && (
                <div className="eyw-card p-5" data-testid="gauge-rec-events">
                  <p className="eyw-label mb-2.5 flex items-center gap-1.5"><Calendar size={13} /> Events & gatherings</p>
                  <ul className="space-y-2.5">
                    {recs.events.map((e) => (
                      <li key={e.id}>
                        <Link to={`/events/${e.slug || e.id}`} className="block group" data-testid={`gauge-event-${e.id}`}>
                          <span className="text-[#1A242B] text-sm font-medium group-hover:text-[#144F75] transition-colors">{e.title}</span>
                          <span className="block text-[#8FA89B] text-xs">{fmtDate(e.date)}{e.is_virtual ? " · Virtual" : e.location ? ` · ${e.location}` : ""}{e.price > 0 ? ` · ${money(e.price)}` : " · Free"}</span>
                          <WhyChips why={e.why} testid={`gauge-why-ev-${e.id}`} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {recs.locations?.length > 0 && (
                <div className="eyw-card p-5" data-testid="gauge-rec-locations">
                  <p className="eyw-label mb-2.5 flex items-center gap-1.5"><MapPin size={13} /> Places to visit</p>
                  <ul className="space-y-2.5">
                    {recs.locations.map((l) => (
                      <li key={l.id}>
                        <Link to={l.slug ? `/locations/${l.slug}` : "/locations"} className="block group">
                          <span className="text-[#1A242B] text-sm font-medium group-hover:text-[#144F75] transition-colors">{l.name}</span>
                          <span className="block text-[#8FA89B] text-xs">{[l.city, l.state].filter(Boolean).join(", ")}{l.kind === "flagship" ? " · Flagship center" : ""}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {recs.community?.length > 0 && (
                <div className="eyw-card p-5" data-testid="gauge-rec-community">
                  <p className="eyw-label mb-2.5 flex items-center gap-1.5"><Users size={13} /> Community to join</p>
                  <ul className="space-y-2.5">
                    {recs.community.map((g) => (
                      <li key={g.id}>
                        <Link to="/community" className="block group">
                          <span className="text-[#1A242B] text-sm font-medium group-hover:text-[#144F75] transition-colors">{g.name}</span>
                          {g.description && <span className="block text-[#8FA89B] text-xs line-clamp-1">{toPlainText(g.description)}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {!result.saved && (recs.events?.length > 0 || recs.locations?.length > 0 || recs.community?.length > 0) && (
          <div className="mt-6 rounded-2xl border border-[#D98E73]/30 bg-[#D98E73]/[0.07] p-6 flex flex-wrap items-center justify-between gap-4" data-testid="gauge-guest-save-pathways">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#D98E73] text-white shrink-0"><Sparkles size={20} /></span>
              <div>
                <p className="font-serif text-lg text-[#1A242B]">Keep these pathways close</p>
                <p className="text-[#4A5D6B] text-sm">Create a free Discovery account to save this reflection — and the events, places, and circles it points you toward — so they're waiting when you're ready.</p>
              </div>
            </div>
            <Link to="/register" data-testid="gauge-guest-save-btn" className="eyw-btn whitespace-nowrap"><Sparkles size={15} /> Save with a free account</Link>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 mt-10">
          <button onClick={restart} data-testid="gauge-restart" className="eyw-btn-outline"><RotateCcw size={16} /> Explore again</button>
          {result.saved ? (
            <>
              <Link to="/gauge/journey" data-testid="gauge-view-journey" className="eyw-btn-outline"><LineChart size={16} /> View my journey</Link>
              <span className="text-[#8FA89B] text-sm flex items-center gap-1.5"><Lock size={14} /> Saved privately to your account</span>
            </>
          ) : (
            <Link to="/login" className="text-[#144F75] text-sm underline">Sign in to save your reflection</Link>
          )}
        </div>

        {/* Holistic plan — Mind / Body / Emotional supportive suggestions */}
        {result.suggestions?.length > 0 && (
          <div className="mt-14" data-testid="gauge-suggestions">
            <div className="text-center max-w-2xl mx-auto">
              <p className="eyw-label !text-[#D98E73] mb-2">Your holistic plan</p>
              <h2 className="font-serif text-3xl text-[#1A242B]">Small, supportive steps to enhance your world</h2>
              <p className="text-[#4A5D6B] mt-3">Gentle suggestions for mind, body, and emotional wellbeing — habits, foods, herbs, meditation, nature, and the relationships you tend.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5 mt-8">
              {result.suggestions.map((dim) => {
                const dm = DIM_META[dim.dimension] || DIM_META.mind;
                const DIcon = dm.icon;
                return (
                  <div key={dim.dimension} className="eyw-card p-6" data-testid={`gauge-dim-${dim.dimension}`} style={{ borderTop: `3px solid ${dm.color}` }}>
                    <div className="flex items-center gap-2.5 mb-1">
                      <span className="grid place-items-center h-10 w-10 rounded-xl text-white" style={{ background: dm.color }}><DIcon size={20} /></span>
                      <h3 className="font-serif text-2xl text-[#1A242B]">{dim.label}</h3>
                    </div>
                    <p className="text-[#8FA89B] text-sm mb-4">{dim.blurb}</p>
                    <div className="space-y-4">
                      {(dim.categories || []).map((cat, ci) => {
                        const CIcon = CAT_ICON[cat.category] || FlaskConical;
                        return (
                          <div key={ci}>
                            <p className="text-xs font-semibold text-[#144F75] uppercase tracking-wide flex items-center gap-1.5 mb-1.5"><CIcon size={13} /> {cat.category}</p>
                            <ul className="space-y-1">
                              {(cat.items || []).map((it, ii) => (
                                <li key={ii} className="text-sm text-[#4A5D6B] flex gap-2"><span className="text-[#D98E73] mt-0.5">·</span> {it}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save / membership prompt */}
            <div className="mt-8 rounded-2xl bg-[#144F75] text-white p-8 text-center" data-testid="gauge-save-plan">
              <Sparkles size={26} className="mx-auto text-[#D98E73] mb-3" />
              {result.saved ? (
                <>
                  <h3 className="font-serif text-2xl">Your journey is saved — keep going</h3>
                  <p className="text-white/80 mt-2 mb-5">As a Discovery Member you can retake GAUGE each week, watch how you change over time, and get a daily coach to support the small steps.</p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/gauge/journey" data-testid="gauge-plan-journey" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">See my journey</Link>
                    <Link to="/coach" className="eyw-btn-ghost !text-white !border-white/30">Open Daily Coach</Link>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-serif text-2xl">Save your plan & track your journey</h3>
                  <p className="text-white/80 mt-2 mb-5">Create a free Discovery account to save these suggestions and revisit them anytime.</p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/register" data-testid="gauge-save-register" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">Create a free account</Link>
                    <Link to="/memberships" className="eyw-btn-ghost !text-white !border-white/30">See what membership adds</Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {bookingService && <BookingModal service={bookingService} onClose={() => setBookingService(null)} />}
      </div>
    );
  }

  // ---------------------------------------------------------------- QUESTIONS
  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-10 pb-32" data-testid="gauge-questions">
      <div className="flex items-center justify-between mb-2">
        <p className="eyw-label">{selectedPath?.title}</p>
        <span className="text-sm text-[#8FA89B]" data-testid="gauge-progress-label">{progressInfo.number} of {progressInfo.total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#E5E0D8] overflow-hidden mb-8">
        <span className="block h-full bg-[#144F75] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {fetching || !current ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={30} /></div>
      ) : (
        <div key={current.id} className="animate-[fadeIn_0.4s_ease]">
          {transition && (
            <p className="text-sm text-[#5C8A72] italic mb-3 flex items-center gap-2" data-testid="gauge-transition">
              <span className="h-px w-6 bg-[#5C8A72]/40" /> {transition}
            </p>
          )}
          <div className="eyw-card p-8">
          <h2 className="font-serif text-2xl text-[#1A242B] leading-snug">{current.question_text}</h2>
          {current.helper_text && <p className="text-[#8FA89B] text-sm mt-2">{current.helper_text}</p>}

          <div className="mt-6">
            {current.question_type === "frequency" && (
              <div className="space-y-2" data-testid="gauge-q-frequency">
                {scale.map((opt) => (
                  <OptionRow key={opt.key} active={responses[current.id] === opt.key} label={opt.label} onClick={() => setAnswer(current.id, opt.key)} testid={`gauge-opt-${opt.key}`} />
                ))}
              </div>
            )}

            {current.question_type === "single_choice" && (
              <div className="space-y-2" data-testid="gauge-q-single">
                {(current.options || []).map((opt) => (
                  <OptionRow key={opt.key} active={responses[current.id] === opt.key} label={opt.label} onClick={() => setAnswer(current.id, opt.key)} testid={`gauge-opt-${opt.key}`} />
                ))}
              </div>
            )}

            {current.question_type === "multi_select" && (
              <div className="space-y-2" data-testid="gauge-q-multi">
                {(current.options || []).map((opt) => {
                  const arr = Array.isArray(responses[current.id]) ? responses[current.id] : [];
                  const active = arr.includes(opt.key);
                  return (
                    <OptionRow key={opt.key} active={active} label={opt.label} testid={`gauge-opt-${opt.key}`}
                      onClick={() => setAnswer(current.id, active ? arr.filter((k) => k !== opt.key) : [...arr, opt.key])} />
                  );
                })}
              </div>
            )}

            {current.question_type === "textarea" && (
              <textarea
                data-testid="gauge-q-textarea"
                rows={4}
                value={responses[current.id] || ""}
                onChange={(e) => setAnswer(current.id, e.target.value)}
                placeholder={current.placeholder_text || "Share a few words…"}
                className="eyw-input w-full resize-none"
              />
            )}
          </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <button onClick={back} data-testid="gauge-back" className="eyw-btn-outline"><ArrowLeft size={16} /> {history.length === 0 ? "Change focus" : "Back"}</button>
        <div className="flex items-center gap-2">
          <button onClick={notSure} disabled={fetching || !current} data-testid="gauge-not-sure" className="text-sm text-[#4A5D6B] hover:text-[#144F75] px-2 py-2">I'm not sure</button>
          <button onClick={skip} disabled={fetching || !current} data-testid="gauge-skip" className="text-sm text-[#4A5D6B] hover:text-[#144F75] px-2 py-2">Skip</button>
          <button onClick={() => next()} disabled={fetching} data-testid="gauge-next" className="eyw-btn disabled:opacity-60">
            {progressInfo.number >= progressInfo.total ? "Review answers" : "Continue"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function WhyChips({ why = [], testid }) {
  if (!why?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testid}>
      {why.map((w) => <span key={w} className="text-[11px] px-2 py-0.5 rounded-full bg-[#8FA89B]/15 text-[#4E6E5E]">{w}</span>)}
    </div>
  );
}

function MatchContext({ ctx }) {
  if (!ctx) return null;
  const budget = { "Free options only": "that are free", "Under $50": "under $50", "$50–$150": "up to $150" }[ctx.budget_label];
  const bits = [ctx.mode === "virtual" ? "online first" : ctx.center_name ? `at ${ctx.center_name} first` : ctx.mode === "in_person" ? "in person first" : "",
    budget || ""].filter(Boolean);
  if (!bits.length) return null;
  return (
    <p className="text-sm text-[#4A5D6B] mb-5" data-testid="gauge-match-context">
      Showing options {bits.join(", ")}.{ctx.hidden_over_budget > 0 && ` ${ctx.hidden_over_budget} more ${ctx.hidden_over_budget === 1 ? "option is" : "options are"} above that budget — browse all offerings anytime.`}
    </p>
  );
}

function OptionRow({ active, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={!!active}
      className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${active ? "border-[#144F75] bg-[#144F75]/[0.06] text-[#144F75] font-medium" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
      {label}
    </button>
  );
}
